import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ApolloError, NetworkStatus, useMutation } from '@apollo/client';
import { useGraphql } from 'utils/hooks/useGraphql';
import { ASCENDING } from 'constants/sortDirections';
import {
  fetchTelnyxVerifyProfilesQuery,
  fetchTwilioVerificationServicesQuery,
  fetchVerificationProfilesQuery,
} from 'api/queries/verificationProfiles';
import {
  createVerificationProfileMutation,
  deleteVerificationProfileMutation,
} from 'api/mutations/sendingPlatforms';
import { SERVICE_PROVIDERS, SmsEspAccount } from 'modules/graphql/types';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { Table, sortRows } from 'ui/table';
import { ColumnData } from 'ui/tableHeader';
import { TableHeader } from 'ui/tableHeader/TableHeader';
import { TableRows } from 'ui/tableRows/TableRows';
import { body2, colors, fontSizes, spacing } from 'ui/theme';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import styled from 'styled-components';
import { SMS_VERIFICATION_PROFILE_TERM } from '../../../../constants';
import { useSendingPlatformsQueryContext } from '../../../../contexts';
import {
  FetchVerificationProfilesResponse,
  TelnyxVerifyProfile,
  TwilioVerificationService,
} from '../../../../types';

const Cell = styled.div`
  ${body2};
  box-sizing: border-box;
  display: table-cell;
  overflow-y: visible;
  padding: ${spacing.threeQuarter};
  margin: 0;
  vertical-align: middle;
`;

const CellTextSecondary = styled.div`
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s11};
  line-height: ${spacing.base};
  margin-top: ${spacing.half};
`;

const StyledButton = styled(Button)`
  width: ${spacing.x6};
  white-space: nowrap;
`;

type Props = {
  espAccount: SmsEspAccount;
};

type ProviderVerificationProfile = TelnyxVerifyProfile | TwilioVerificationService;

type RowProps = {
  row: ProviderVerificationProfile;
};

export const VerificationProfilesTable = ({ espAccount }: Props) => {
  const dispatch = useDispatch();
  const { refetchVerificationProfiles } = useSendingPlatformsQueryContext();
  const {
    data: connectedVerificationProfilesResponse,
    refetch: refetchConnectedVerificationProfiles,
    networkStatus: connectedVerificationProfilesNetworkStatus,
    errorMessage: connectedVerificationProfilesError,
  } = useGraphql<FetchVerificationProfilesResponse>(fetchVerificationProfilesQuery, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const connectedVerificationProfiles =
    connectedVerificationProfilesResponse?.verificationProfiles?.filter(
      (profile) => profile.espAccountId === espAccount.id,
    );
  const doRefetch = () => {
    // when there's a change, we need to refetch not only the current table, but also refresh the state in the rest of the page
    refetchConnectedVerificationProfiles();
    refetchVerificationProfiles();
  };
  const refetching = connectedVerificationProfilesNetworkStatus === NetworkStatus.refetch;
  const [actionInProgress, setActionInProgress] = useState(false);

  const findConnectedProfileForProfile = (row: ProviderVerificationProfile) =>
    (connectedVerificationProfiles || []).find((profile) => profile.profileId === row.id);

  const isTelnyx = espAccount.esp === SERVICE_PROVIDERS.TELNYX;
  const isTwilio = espAccount.esp === SERVICE_PROVIDERS.TWILIO;

  const query = isTelnyx ? fetchTelnyxVerifyProfilesQuery : fetchTwilioVerificationServicesQuery;

  const {
    data: providerVerificationProfilesResponse,
    isLoading: loadingProviderVerifyProfiles,
    errorMessage: providerVerifyProfilesError,
  } = useGraphql(query, {
    fetchPolicy: 'no-cache',
    variables: {
      espAccountId: espAccount.id,
    },
  });
  const providerVerificationProfiles =
    providerVerificationProfilesResponse?.telnyxVerifyProfiles ||
    providerVerificationProfilesResponse?.twilioVerificationServices ||
    [];

  const [sortedBy, setSortedBy] = useState('id');
  const [sortDirection, setSortDirection] = useState(ASCENDING);

  const sortedRows = useMemo(() => {
    return sortRows(providerVerificationProfiles, sortedBy, sortDirection);
  }, [providerVerificationProfiles, sortDirection, sortedBy]);

  const [createProfileMutation] = useMutation(createVerificationProfileMutation, {
    // refetchQueries: ['FetchVerificationProfiles'],
    onCompleted({ createVerificationProfile }) {
      const createdId = createVerificationProfile.id;
      dispatch(
        addPositiveToast(
          `Successfully created ${SMS_VERIFICATION_PROFILE_TERM} with ID ${createdId}.`,
        ),
      );
      doRefetch();
      setActionInProgress(false);
    },
    onError(graphqlError: ApolloError) {
      dispatch(
        addNegativeToast(
          graphqlError?.message || `Unable to create ${SMS_VERIFICATION_PROFILE_TERM}`,
        ),
      );
      setActionInProgress(false);
    },
  });

  const [deleteProfileMutation] = useMutation(deleteVerificationProfileMutation, {
    // refetchQueries: ['FetchVerificationProfiles'],
    // the version below doesn't seem to work correctly; though it does a refetch, it doesn't actually change any values for some reason
    // refetchQueries: [{ query: fetchVerificationProfilesQuery }],
    onCompleted({ deleteVerificationProfile }: { deleteVerificationProfile: boolean }) {
      if (deleteVerificationProfile) {
        dispatch(addPositiveToast(`Deleted ${SMS_VERIFICATION_PROFILE_TERM}`));
        doRefetch();
      } else {
        dispatch(addNegativeToast(`Unable to delete ${SMS_VERIFICATION_PROFILE_TERM}`));
      }
      setActionInProgress(false);
    },
    onError(graphqlError: ApolloError) {
      dispatch(
        addNegativeToast(
          graphqlError?.message || `Unable to create ${SMS_VERIFICATION_PROFILE_TERM}`,
        ),
      );
      setActionInProgress(false);
    },
  });

  const handleClickConnect = (row: ProviderVerificationProfile) => {
    setActionInProgress(true);
    createProfileMutation({
      variables: {
        espAccountId: espAccount.id,
        profileId: row.id,
      },
    });
  };

  const handleClickDisconnect = (row: ProviderVerificationProfile) => {
    // TODO - add a confirm
    setActionInProgress(true);
    deleteProfileMutation({
      variables: {
        id: findConnectedProfileForProfile(row)?.id,
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        name: 'Name',
        value: 'name',
        align: 'flex-start',
        RowCell: ({ row }: RowProps) => (
          <Cell>
            <div>{row.name}</div>
            <CellTextSecondary>{row.id}</CellTextSecondary>
          </Cell>
        ),
      } as ColumnData,
      {
        name: 'Updated',
        value: 'updatedAt',
        align: 'flex-start',
        RowCell: ({ row }: RowProps) => <Cell>{new Date(row.updatedAt).toString()}</Cell>,
      } as ColumnData,
      {
        name: 'Token Length',
        value: 'codeLength',
        align: 'flex-start',
        RowCell: ({ row }: RowProps) => (
          // Telnyx verification token is always fixed length
          <Cell>{isTwilio ? (row as TwilioVerificationService).codeLength : '5'}</Cell>
        ),
      } as ColumnData,
      {
        name: 'Actions',
        value: '',
        align: 'flex-start',
        RowCell: ({ row }: RowProps) => {
          const connectedProfileFound =
            connectedVerificationProfiles !== undefined &&
            findConnectedProfileForProfile(row) !== undefined;
          const handleClick = () =>
            connectedProfileFound ? handleClickDisconnect(row) : handleClickConnect(row);
          const buttonVariant = connectedProfileFound
            ? BUTTON_VARIANTS.DESTRUCTIVE
            : BUTTON_VARIANTS.PRIMARY;

          return (
            <Cell>
              <StyledButton
                data-test="verification-profile-connection-button"
                isDisabled={actionInProgress || refetching}
                onClick={handleClick}
                variant={buttonVariant}
              >
                {connectedProfileFound ? 'Disconnect' : 'Connect'}
              </StyledButton>
            </Cell>
          );
        },
      } as ColumnData,
    ],
    [connectedVerificationProfiles, actionInProgress, connectedVerificationProfilesNetworkStatus],
  );

  if (loadingProviderVerifyProfiles) return <p>Loading...</p>;
  if (connectedVerificationProfilesError)
    return <p>Unable to fetch currently connected services</p>;
  if (providerVerifyProfilesError)
    return (
      <p>
        Unable to connect to provider using supplied credentials. (
        {providerVerifyProfilesError.message || 'No additional info'})
      </p>
    );

  if (providerVerificationProfiles.length === 0)
    return <p>No services configured in provider account.</p>;

  return (
    <Table>
      <TableHeader
        columns={columns}
        sortedBy={sortedBy}
        setSortedBy={setSortedBy}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
      />
      <TableRows rows={sortedRows} columns={columns} />
    </Table>
  );
};
